import type {
	PublicEvent,
	PublicLoanAuditSummary,
	PublicLoanRespa,
} from "@homesite/api-contract";
import { queryKeys } from "@homesite/queries";
import { useQueries } from "@tanstack/react-query";
import { useApiClient } from "../lib/ApiClientContext";
import { NotFoundError, retryUnlessNotFound } from "../lib/notFound";
import type { PaginationInfo } from "./useLoanTimeline";

export interface LoanAuditData {
	summary: PublicLoanAuditSummary | null;
	respa: PublicLoanRespa | null;
	events: PublicEvent[];
	pagination: PaginationInfo | null;
	loading: boolean;
	/**
	 * Fatal error — set only when nothing could be loaded from any source, i.e.
	 * the loan genuinely has no records. When at least one source succeeds the
	 * page renders partial data instead of failing whole.
	 */
	error: Error | null;
	/** Per-source errors so the page can render whatever loaded. */
	summaryError: Error | null;
	respaError: Error | null;
	timelineError: Error | null;
}

/**
 * Composed hook for the loan audit page — fetches summary, RESPA status, and
 * the first page of the timeline in parallel via useQueries.
 */
export function useLoanAudit(loanId: string): LoanAuditData {
	const client = useApiClient();

	const results = useQueries({
		queries: [
			{
				queryKey: queryKeys.publicVerification.loans.auditSummary(loanId),
				queryFn: async () => {
					const result = await client.publicVerification.getLoanAuditSummary({
						params: { loanId },
					});
					if (result.status === 200) {
						return result.body;
					}
					if (result.status === 404) {
						throw new NotFoundError("Loan not found");
					}
					throw new Error("Failed to fetch audit summary");
				},
				retry: retryUnlessNotFound(2),
				enabled: loanId.length > 0,
			},
			{
				queryKey: queryKeys.publicVerification.loans.respa(loanId),
				queryFn: async () => {
					const result = await client.publicVerification.getLoanRespa({
						params: { loanId },
					});
					if (result.status === 200) {
						return result.body;
					}
					if (result.status === 404) {
						throw new NotFoundError("Loan not found");
					}
					throw new Error("Failed to fetch RESPA data");
				},
				retry: retryUnlessNotFound(2),
				enabled: loanId.length > 0,
			},
			{
				queryKey: queryKeys.publicVerification.loans.timeline(
					loanId,
					1,
					20,
					undefined,
				),
				queryFn: async () => {
					const result = await client.publicVerification.getLoanTimeline({
						params: { loanId },
						query: { page: 1, limit: 20 },
					});
					if (result.status === 200) {
						return result.body;
					}
					if (result.status === 404) {
						throw new NotFoundError("Loan not found");
					}
					throw new Error("Failed to fetch loan timeline");
				},
				retry: retryUnlessNotFound(2),
				enabled: loanId.length > 0,
			},
		],
	});

	const [summaryQuery, respaQuery, timelineQuery] = results;

	const loading = results.some((r) => r.isLoading);

	const summaryError = (summaryQuery.error as Error | null) ?? null;
	const respaError = (respaQuery.error as Error | null) ?? null;
	const timelineError = (timelineQuery.error as Error | null) ?? null;

	// Render partial data whenever any source succeeded; only surface a fatal
	// error once everything has settled and nothing loaded.
	const hasAnyData =
		summaryQuery.isSuccess || respaQuery.isSuccess || timelineQuery.isSuccess;
	const firstError = summaryError ?? respaError ?? timelineError;
	const error = loading || hasAnyData ? null : firstError;

	// `useQueries` collapses each query's data to `unknown`, so narrow the
	// timeline page once here rather than re-casting per field.
	const timelineData = timelineQuery.data as
		| {
				items: PublicEvent[];
				total: number;
				page: number;
				limit: number;
				totalPages: number;
		  }
		| undefined;

	return {
		summary: (summaryQuery.data as PublicLoanAuditSummary | undefined) ?? null,
		respa: (respaQuery.data as PublicLoanRespa | undefined) ?? null,
		events: timelineData?.items ?? [],
		pagination: timelineData
			? {
					page: timelineData.page,
					limit: timelineData.limit,
					total: timelineData.total,
					totalPages: timelineData.totalPages,
				}
			: null,
		loading,
		error,
		summaryError,
		respaError,
		timelineError,
	};
}
